import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactDOMServer from "react-dom/server";

// region Imports - Components
import NewDataTable, { DataTableButtons, DataTableColumns, DataTableActions } from "@molecules/NewDataTable";
import QueryFilterIntegrationsReport from "@organisms/QueryFilterIntegrationsReport";
// endregion Imports - Components
// region Imports - External Libraries
import { Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText } from "@material-ui/core";
import * as dateFns from "date-fns";
// endregion Imports - External Libraries
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  DataTableMessages,
  TravelHistoryMessages
} from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
// endregion Imports - Services
// region Imports - Shared
import utils from "@utils/useful-functions";
import { IntegrationLogListItemResponseDto } from "@shared/dtos/integration-logs/integration-log-list-item-response.dto";
// endregion Imports - Shared
// region Imports - Styles
import { Container, ClickableCell } from "./styles";
// endregion Imports - Styles

const IntegrationReport: React.FC = () => {

  // region Hooks
  const { screen } = useSelector((screen: any) => screen);
  const { t } = useTranslation();
  // endregion Hooks
  // region States
  const [logs, setLogs] = useState<IntegrationLogListItemResponseDto[]>([] as Array<IntegrationLogListItemResponseDto>);
  const [openFilterFields, setOpenFilterFields] = useState(true);
  const [numberOfFilteredOptions, setNumberOfFilteredOptions] = useState(0);

  // States for the modal (Load Group NF's)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<string[]>([]);

  // Used to create export file name
  const [startDate, setStartDate] = useState<string>(dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd").concat("T00:00"));
  const [finishDate, setFinishDate] = useState<string>(dateFns.format(new Date(), "yyyy-MM-dd").concat("T23:59"));
  // endregion States
  // region Constants
  const dataTableSettings: DataTables.Settings = {
    order: [[4, "asc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };
  const dataTableColumns: DataTableColumns[] = [
    {
      title: t(TravelHistoryMessages.integrationReport_column_numNf),
      data: (log: IntegrationLogListItemResponseDto) => log.num_nf,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_statusNf),
      data: (log: IntegrationLogListItemResponseDto) => log.status_nf,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_emissionType),
      data: (log: IntegrationLogListItemResponseDto) => log.load_group,
      defaultContent: "",
      orderable: true,
      filterable: true,
      render: (data) => (data ? "Lote" : "Convencional")
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_emission),
      data: (log: IntegrationLogListItemResponseDto) => log.document_date,
      defaultContent: "",
      orderable: true,
      filterable: true,
      render: (data) => (data ? (dateFns.format(new Date(data), "dd/MM/yyyy HH:mm")) : "")
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_integration),
      data: (log: IntegrationLogListItemResponseDto) => log.integration_date,
      defaultContent: "",
      orderable: true,
      filterable: true,
      render: (data) => (data ? (dateFns.format(new Date(data), "dd/MM/yyyy HH:mm")) : "")
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_loadGroup),
      data: (log: IntegrationLogListItemResponseDto) => log.load_group,
      defaultContent: "",
      orderable: true,
      filterable: true,
      render: (data: string | null) => {
        if (!data) return "";
        const items = data.split(", ");

        return ReactDOMServer.renderToString(
          <ClickableCell className="load-group-details-button">
            {items.length} NFs
          </ClickableCell>
        );
      }
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_vehicle),
      data: (log: IntegrationLogListItemResponseDto) => log.vehicle?.license_plate,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_driverCode),
      data: (log: IntegrationLogListItemResponseDto) => log.driver?.code,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_driverDocument),
      data: (log: IntegrationLogListItemResponseDto) => log.driver?.document,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_origin),
      data: (log: IntegrationLogListItemResponseDto) => log.origin,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_destination),
      data: (log: IntegrationLogListItemResponseDto) => log.destination,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_client),
      data: (log: IntegrationLogListItemResponseDto) => log.client,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_productType),
      data: (log: IntegrationLogListItemResponseDto) => log.product_type,
      defaultContent: "",
      orderable: true,
      filterable: true
    },
    {
      title: t(TravelHistoryMessages.integrationReport_column_errorCode),
      data: (log: IntegrationLogListItemResponseDto) => log.code_error,
      defaultContent: "",
      orderable: true,
      filterable: true
    }
  ];
  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_integrações_${startDate}__${finishDate}`,
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    }
  ];
  const dataTableActions: DataTableActions[] = [
    {
      ref: ".load-group-details-button",
      callback: (rowData: IntegrationLogListItemResponseDto) => handleOpenLoadGroupModal(rowData.load_group)
    }
  ];
  // endregion Constants
  // region Functions

  /**
   * Updates the application state based on the filter operation applied.
   *
   * @param {number} numberOfFilteredOptions - The number of options filtered by the user.
   * @param {IntegrationLogListItemResponseDto[]} returnedData - The filtered data returned after applying the filter.
   * @param {string} startDate - The start date of the filter range.
   * @param {string} finishDate - The end date of the filter range.
   */
  const handleApplyFilter = useCallback((
    numberOfFilteredOptions: number,
    returnedData: IntegrationLogListItemResponseDto[],
    startDate: string,
    finishDate: string
  ) => {
    setNumberOfFilteredOptions(numberOfFilteredOptions);
    setLogs(returnedData);
    setStartDate(startDate);
    setFinishDate(finishDate);
  }, [setNumberOfFilteredOptions, setLogs, setStartDate, setFinishDate]); // Dependencies for useCallback

  /**
   * Handles the opening of the Load Group Modal.
   *
   * This function processes the provided load group data by splitting it into an array
   * of strings based on a comma and space (", "). If the data is valid (non-null), it sets
   * the modal content to the processed data and opens the modal.
   *
   * @param {string | null} loadGroupData - The data for the load group, passed as a string.
   *                                        If null, no action will be taken.
   */
  const handleOpenLoadGroupModal = (loadGroupData: string | null) => {
    if (loadGroupData) {
      setModalContent(loadGroupData.split(", "));
      setModalOpen(true);
    }
  };

  // Handles the closing of the modal (Load Group NF's)
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalContent([]);
  };
  // endregion Functions

  return (
    <Container platform={screen.platform as ScreenPlatform} className="page">
      <NewDataTable
        title={t(TravelHistoryMessages.integrationReport)}
        filters
        data={logs}
        columns={dataTableColumns}
        settings={dataTableSettings}
        buttons={dataTableButtons}
        actions={dataTableActions}
        onClickFilterButton={() => setOpenFilterFields(true)}
        numberOfFilteredOptions={numberOfFilteredOptions}
      />
      <QueryFilterIntegrationsReport
        open={openFilterFields}
        onClose={() => setOpenFilterFields(false)}
        onFilter={handleApplyFilter}
      />
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="load-group-dialog-title"
        scroll="paper"
      >
        <DialogTitle id="load-group-dialog-title">
          {t(TravelHistoryMessages.integrationReport_loadGroupModalTitle)}
        </DialogTitle>
        <DialogContent>
          <List>
            {modalContent.map((nf) => (
              <ListItem key={nf}>
                <ListItemText primary={nf} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default IntegrationReport;
